import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AuthResult,
  AuthUser,
  completeKakaoSignup,
  getKakaoAuthUrl,
  kakaoLogin,
  login as loginRequest,
  refresh as refreshRequest,
  register as registerRequest,
  updateNickname as updateNicknameRequest,
} from '../services/authService';
import { invalidateMyPageCache } from '../services/mypageService';
import { invalidateTradingHistoryCache } from '../features/trading-history/services/tradingHistoryService';
import rankingCache from '../utils/rankingCache';
import { decodeJwt, isTokenExpired } from '../utils/jwt';
import {
  loadAccessToken,
  loadRefreshToken,
  loadUser,
  saveAccessToken,
  saveRefreshToken,
  saveUser,
} from '../utils/storage';

interface PendingSignupData {
  tempToken: string;
  email: string;
  nickname: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isInitializing: boolean;
  pendingSignupData: PendingSignupData | null;
  login(email: string, password: string): Promise<void>;
  register(email: string, password: string, nickname: string): Promise<void>;
  loginWithKakao(): Promise<void>;
  handleKakaoCallback(code: string): Promise<void>;
  completeSignup(): Promise<void>;
  cancelSignup(): void;
  logout(): void;
  refreshAccessToken(): Promise<string | null>;
  updateUser(user: AuthUser): void;
  updateNickname(nickname: string): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const MIN_REFRESH_DELAY = 5_000;
const REFRESH_LEAD_TIME = 60_000;

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [pendingSignupData, setPendingSignupData] = useState<PendingSignupData | null>(null);

  const refreshTimeoutRef = useRef<number | null>(null);
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  const applyAuthResult = useCallback((result: AuthResult | null) => {
    if (result) {
      setUser(result.user);
      setAccessToken(result.accessToken ?? null);
      setRefreshToken(result.refreshToken ?? null);

      saveUser(result.user);
      saveAccessToken(result.accessToken ?? null);
      saveRefreshToken(result.refreshToken ?? null);
    } else {
      setUser(null);
      setAccessToken(null);
      setRefreshToken(null);

      saveUser(null);
      saveAccessToken(null);
      saveRefreshToken(null);
    }
  }, []);

  const refreshAccessTokenRef =
    useRef<() => Promise<string | null> | null>(null);

  const scheduleRefresh = useCallback(
    (token: string | null, nextRefreshToken: string | null) => {
      clearRefreshTimer();

      if (!token || !nextRefreshToken) {
        return;
      }

      const payload = decodeJwt(token);
      const expiryTime = payload?.exp ? payload.exp * 1000 : null;

      if (!expiryTime) {
        return;
      }

      const now = Date.now();
      const delay = Math.max(
        expiryTime - now - REFRESH_LEAD_TIME,
        MIN_REFRESH_DELAY
      );

      refreshTimeoutRef.current = window.setTimeout(() => {
        refreshAccessTokenRef.current?.();
      }, delay);
    },
    [clearRefreshTimer]
  );

  const logout = useCallback(() => {
    clearRefreshTimer();
    refreshPromiseRef.current = null;
    
    // 모든 캐시 무효화
    invalidateMyPageCache();
    invalidateTradingHistoryCache(); // 전체 매매내역 캐시 무효화
    rankingCache.invalidateCache(); // 랭킹 캐시 무효화
    
    applyAuthResult(null);
  }, [applyAuthResult, clearRefreshTimer]);

  const refreshAccessToken = useCallback(async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const tokenToUse = refreshToken ?? loadRefreshToken();
    if (!tokenToUse) {
      return null;
    }

    const refreshPromise = (async () => {
      try {
        const result = await refreshRequest(tokenToUse);
        applyAuthResult(result);
        scheduleRefresh(
          result.accessToken ?? null,
          result.refreshToken ?? null
        );
        return result.accessToken ?? null;
      } catch (error) {
        console.error('액세스 토큰 갱신 실패:', error);
        logout();
        // 인증 만료 이벤트 발생 (로그인 모달 표시)
        window.dispatchEvent(new CustomEvent('auth-expired'));
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    refreshPromiseRef.current = refreshPromise;
    return refreshPromise;
  }, [applyAuthResult, logout, refreshToken, scheduleRefresh]);

  useEffect(() => {
    refreshAccessTokenRef.current = refreshAccessToken;
  }, [refreshAccessToken]);

  const handleAuthSuccess = useCallback(
    (result: AuthResult) => {
      applyAuthResult(result);
      scheduleRefresh(result.accessToken ?? null, result.refreshToken ?? null);
    },
    [applyAuthResult, scheduleRefresh]
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await loginRequest(email, password);
      handleAuthSuccess(result);
    },
    [handleAuthSuccess]
  );

  const register = useCallback(
    async (email: string, password: string, nickname: string) => {
      const result = await registerRequest(email, password, nickname);
      handleAuthSuccess(result);
    },
    [handleAuthSuccess]
  );

  const loginWithKakao = useCallback(async () => {
    try {
      const url = await getKakaoAuthUrl();
      // 카카오 로그인 페이지로 리다이렉트
      window.location.href = url;
    } catch (error) {
      console.error('카카오 로그인 URL 가져오기 실패:', error);
      throw error;
    }
  }, []);

  const handleKakaoCallback = useCallback(
    async (code: string) => {
      try {
        const response: any = await kakaoLogin(code);
        
        console.log('📦 카카오 로그인 응답:', response);
        
        // needsConsent 체크 (신규 유저) - data 안에서 확인
        if (response.needsConsent === true) {
          console.log('🆕 신규 사용자 - 동의 팝업 표시');
          console.log('   - tempToken:', response.tempToken);
          console.log('   - email:', response.pendingUserData?.email);
          console.log('   - nickname:', response.pendingUserData?.nickname);
          
          setPendingSignupData({
            tempToken: response.tempToken || '',
            email: response.pendingUserData?.email || '',
            nickname: response.pendingUserData?.nickname || '',
          });
        } else {
          // 기존 유저 - 바로 로그인
          console.log('✅ 기존 사용자 - 로그인 완료');
          
          // normalizeAuthResponse로 파싱
          const authResult: AuthResult = {
            user: response.user,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
          };
          
          handleAuthSuccess(authResult);
        }
      } catch (error) {
        console.error('❌ 카카오 로그인 콜백 처리 실패:', error);
        throw error;
      }
    },
    [handleAuthSuccess]
  );

  const completeSignup = useCallback(async () => {
    if (!pendingSignupData) {
      throw new Error('대기 중인 가입 정보가 없습니다.');
    }

    try {
      const result = await completeKakaoSignup(
        pendingSignupData.tempToken,
        true, // agreedToPrivacy
        true, // agreedToTerms
      );
      
      handleAuthSuccess(result);
      setPendingSignupData(null);
    } catch (error) {
      console.error('가입 완료 처리 실패:', error);
      throw error;
    }
  }, [pendingSignupData, handleAuthSuccess]);

  const cancelSignup = useCallback(() => {
    setPendingSignupData(null);
  }, []);

  const updateUser = useCallback((nextUser: AuthUser) => {
    setUser(nextUser);
    saveUser(nextUser);
  }, []);

  const updateNickname = useCallback(
    async (nickname: string) => {
      try {
        const updatedUser = await updateNicknameRequest(nickname);
        // 기존 사용자 정보와 병합하여 업데이트
        if (user) {
          const mergedUser: AuthUser = {
            ...user,
            nickname: updatedUser.nickname,
          };
          updateUser(mergedUser);
        }
      } catch (error) {
        console.error('닉네임 변경 실패:', error);
        throw error;
      }
    },
    [user, updateUser]
  );

  useEffect(() => {
    const savedUser = loadUser<AuthUser>();
    const savedAccessToken = loadAccessToken();
    const savedRefreshToken = loadRefreshToken();

    if (savedUser) {
      setUser(savedUser);
    }

    if (savedAccessToken) {
      setAccessToken(savedAccessToken);
      saveAccessToken(savedAccessToken);
    }

    if (savedRefreshToken) {
      setRefreshToken(savedRefreshToken);
    }

    const needsRefresh =
      savedAccessToken &&
      savedRefreshToken &&
      isTokenExpired(savedAccessToken, REFRESH_LEAD_TIME / 2);

    if (needsRefresh) {
      refreshAccessToken().finally(() => setIsInitializing(false));
    } else {
      scheduleRefresh(savedAccessToken, savedRefreshToken);
      setIsInitializing(false);
    }

    return () => {
      clearRefreshTimer();
    };
  }, [clearRefreshTimer, refreshAccessToken, scheduleRefresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      accessToken,
      refreshToken,
      isInitializing,
      pendingSignupData,
      login,
      register,
      loginWithKakao,
      handleKakaoCallback,
      completeSignup,
      cancelSignup,
      logout,
      refreshAccessToken,
      updateUser,
      updateNickname,
    }),
    [
      user,
      accessToken,
      refreshToken,
      isInitializing,
      pendingSignupData,
      login,
      register,
      loginWithKakao,
      handleKakaoCallback,
      completeSignup,
      cancelSignup,
      logout,
      refreshAccessToken,
      updateUser,
      updateNickname,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      'useAuthContext는 AuthProvider 내부에서만 사용할 수 있습니다.'
    );
  }
  return context;
};
